import { ReferenceArea, ReferenceLine } from "recharts";
import type React from "react";
import type { MeasurementGoal, ReferenceRange } from "@/types/measurement";
const GOAL_COLOR = "rgb(var(--status-success-text))";
const DEFAULT_FILL_OPACITY = 0.08;

/**
 * Returns ReferenceArea/ReferenceLine elements for reference ranges.
 *
 * Like `cartesianElements`, returns a ReactNode[] that must be spread as
 * direct children of a Recharts chart component.
 */
export function referenceRangeElements(referenceRanges: ReferenceRange[] | null | undefined, options?: {
  showLabels?: boolean;
  fillOpacity?: number;
}): React.ReactNode[] {
  if (!referenceRanges?.length) return [];
  const showLabels = options?.showLabels ?? true;
  const fillOpacity = options?.fillOpacity ?? DEFAULT_FILL_OPACITY;
  return referenceRanges.map((range, i) => {
    if (range.min != null && range.max != null) {
      return <ReferenceArea key={`ref-area-${i}`} y1={range.min} y2={range.max} fill={range.color} fillOpacity={fillOpacity} ifOverflow="hidden" label={showLabels ? {
        value: range.label,
        position: "insideTopLeft" as const,
        fontSize: 10,
        fill: range.color
      } : undefined} />;
    }
    // Single boundary as a dashed line
    const val = range.min ?? range.max;
    if (val != null) {
      return <ReferenceLine key={`ref-line-${i}`} y={val} stroke={range.color} strokeDasharray="5 5" ifOverflow="hidden" label={showLabels ? {
        value: range.label,
        position: "insideTopLeft" as const,
        fontSize: 10,
        fill: range.color
      } : undefined} />;
    }
    return null;
  }).filter(Boolean);
}

/**
 * Returns ReferenceLine elements for a goal.
 */
export function goalElements(goal: MeasurementGoal | null | undefined, options?: {
  showLabels?: boolean;
}): React.ReactNode[] {
  if (!goal) return [];
  const showLabels = options?.showLabels ?? true;
  const goalLabel = goal.isComputed ? "Goal" : goal.label || "Goal";
  const goalKey = goal.isComputed ? `computed-${goal.fieldId}` : goal.id;
  const elements: React.ReactNode[] = [];
  if (goal.goalValue != null) {
    elements.push(<ReferenceLine key={`goal-value-${goalKey}`} y={goal.goalValue} stroke={GOAL_COLOR} strokeWidth={2} strokeDasharray="6 4" ifOverflow="hidden" label={showLabels ? {
      value: goalLabel,
      position: "insideTopRight" as const,
      fontSize: 10,
      fill: GOAL_COLOR
    } : undefined} />);
  }
  if (!goal.isComputed && goal.goalMin != null) {
    elements.push(<ReferenceLine key={`goal-min-${goalKey}`} y={goal.goalMin} stroke={GOAL_COLOR} strokeWidth={2} strokeDasharray="6 4" ifOverflow="hidden" label={showLabels ? {
      value: `${goalLabel} Min`,
      position: "insideTopRight" as const,
      fontSize: 10,
      fill: GOAL_COLOR
    } : undefined} />);
  }
  if (!goal.isComputed && goal.goalMax != null) {
    elements.push(<ReferenceLine key={`goal-max-${goalKey}`} y={goal.goalMax} stroke={GOAL_COLOR} strokeWidth={2} strokeDasharray="6 4" ifOverflow="hidden" label={showLabels ? {
      value: `${goalLabel} Max`,
      position: "insideTopRight" as const,
      fontSize: 10,
      fill: GOAL_COLOR
    } : undefined} />);
  }
  return elements;
}